<script>
  import AbortController from 'abort-controller';
  import SearchOverpanelResults from '../SearchOverpanelResults/SearchOverpanelResults.svelte';
  import OverpanelControl from '../OverpanelControl/OverpanelControl.svelte';
  import { SPRITESHEET_URL } from '../../utils/constants';
  import search from '../../utils/search';
  import getClosest from '../../utils/closest-string';
  import { overpanelMap, closeOverpanel } from '../../stores/overpanelControl';
  import searchValue from '../../stores/search';
  import { KEYS } from '../../utils/constants';
  import { getSearchURL } from '../../utils/search-query.ts';

  let {
    inputPlaceholder,
    searchButtonText,
    title,
    errorText,
    emptyErrorText,
    searchingText,
    searchingCompleteTemplate,
    searchInstructions,
    searchOverpanelResults,
    lang = '',
    dataTestId = 'search-overpanel',
  } = $props();

  let searchResults = $state([]);
  let searchResultsLabel = $state();
  let previousResults = {};
  let controller;
  let isError = $state(false);
  let input = $state();
  let selected = $state();
  let scrollContainer = $state();
  let showInputEmptyMessage = $state(false);
  const resultsId = 'search-overpanel-results-list';

  const { ARROW_DOWN, ARROW_UP, ESCAPE } = KEYS;

  searchValue.subscribe((value) => {
    selected = undefined;
    if (!value) searchResults.length = 0;
  });

  const handleSearch = async (e) => {
    const value = e.target.value;
    showInputEmptyMessage = false;

    if (controller) {
      controller.abort();
    }

    isError = false;

    if (!value) return;

    controller = new AbortController();

    let data;
    searchResultsLabel = searchingText;

    const res = await search({
      value: e.target.value,
      controller,
    }).catch((err) => {
      if (err.name !== 'AbortError') {
        return err;
      }
    });

    if (!res || res.status > 200) {
      return;
    }

    data = await res.json();

    if (data && data.hits) {
      // if results were returned cache them
      // if no results were returned find last closest match
      if (data.hits.length) {
        const emRegex = /<(\/?)em>/g;
        data.hits = data.hits.map((hit) => {
          try {
            hit.htmlName = hit._highlightResult.name.value.replace(emRegex, '<$1strong>');
            hit.htmlFormalName = hit._highlightResult.formalName.value.replace(emRegex, '<$1strong>');
          } catch {
            hit.htmlName = hit.name;
            hit.htmlFormalName = hit.formalName;
          }
          return hit;
        });
        previousResults[value] = data;
      } else {
        const closest = getClosest(value, Object.keys(previousResults));
        if (closest) {
          data = previousResults[closest];
        } else {
          isError = true;
        }
      }

      searchResults.length = 0;
      searchResults.push(...data.hits);

      if (searchResults.length) {
        searchResultsLabel = searchingCompleteTemplate
          .replace(/{{NUMBER}}/, searchResults.length)
          .replace(/{{MEDICATION}}/, searchResults[0].name)
          .replace(/{{TOTAL}}/, searchResults.length);
      } else {
        searchResultsLabel = errorText;
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let firstResult;

    firstResult = searchResults[selected || 0];

    if (!firstResult) {
      if (!$searchValue) showInputEmptyMessage = true;
      return;
    }

    const { slug, genericSlug, name } = firstResult;
    window.location.href = getSearchURL({ slug, genericSlug, name, lang });
  };

  const mountCallback = () => {
    input.focus();
    if ($searchValue) {
      handleSearch({ target: { value: $searchValue } });
    }
  };

  const handleKeydown = (e) => {
    if (![ARROW_UP, ARROW_DOWN, ESCAPE].includes(e.key)) return;

    if (e.key === ESCAPE) {
      if ($searchValue) {
        $searchValue = '';
        e.stopPropagation();
      } else {
        closeOverpanel('search');
      }
      return;
    }

    if (!searchResults.length) return;

    if (selected === undefined) {
      selected = -1;
    }

    selected += e.key === ARROW_DOWN ? 1 : -1;
    if (selected < 0) selected = searchResults.length - 1;
    selected %= searchResults.length;
    e.preventDefault();
  };

  const handleBlur = () => {
    showInputEmptyMessage = false;
  };

  const closeCallback = () => {
    $searchValue = '';
  };
</script>

{#if $overpanelMap.has('search')}
  <OverpanelControl {title} {mountCallback} {closeCallback} overpanelKey="search">
    <div class="flex flex-col h-full bg-gray-mercury" data-testid={dataTestId}>
      <div class="bg-blue-navy py-2 px-4">
        <form class="relative flex items-center justify-between" onsubmit={handleSubmit}>
          <div
            class="flex-1"
            data-testid="{dataTestId}-combobox"
            role="combobox"
            aria-expanded={!!searchResults.length}
            aria-controls={resultsId}
            aria-haspopup="listbox"
          >
            <div aria-live="assertive" aria-label={searchResultsLabel} data-testid="{dataTestId}-combobox-status"></div>
            <input
              id="search-input"
              data-testid="{dataTestId}-input"
              type="search"
              class="w-full py-2 pl-10 pr-4 rounded bg-white border border-gray-gunmetal leading-loose text-base font-normal
              placeholder-gray-bluey focus:relative focus:outline-hidden focus:shadow-focus active:shadow-active"
              class:animate-shake={showInputEmptyMessage}
              placeholder={inputPlaceholder}
              oninput={handleSearch}
              onkeydown={handleKeydown}
              onblur={handleBlur}
              bind:this={input}
              bind:value={$searchValue}
              autocomplete="off"
              aria-autocomplete="list"
              aria-label={searchInstructions}
              aria-activedescendant={selected !== undefined ? `search-result-${selected}` : ''}
            />
          </div>
          <button
            type="submit"
            onblur={handleBlur}
            class="ml-2 rounded border-t border-b px-4 py-2 text-base leading-loose text-center active:bg-blue-malibu
            focus:relative focus:outline-hidden focus:shadow-focus active:shadow-active bg-blue-robin-egg hover-hover:hover:bg-blue-light
            border-blue-robin-egg font-semibold text-black"
          >
            {searchButtonText}
          </button>
          <svg
            class="w-4 h-4 top-4 left-4 absolute fill-current"
            class:animate-shake={showInputEmptyMessage}
            role="presentation"
          >
            <use xlink:href="{SPRITESHEET_URL}#magnifying-glass" />
          </svg>
        </form>
        {#if showInputEmptyMessage}
          <div class="mt-4 mb-2 text-white" role="alert">{emptyErrorText}</div>
        {/if}
      </div>
      <div class="flex flex-col flex-1 overflow-auto" bind:this={scrollContainer}>
        <SearchOverpanelResults
          id={resultsId}
          {...searchOverpanelResults}
          {searchResults}
          {selected}
          {lang}
          {isError}
        />
      </div>
    </div>
  </OverpanelControl>
{/if}
